import { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import {
  getProjects, createProject, updateProject, getNextProjectCode,
  getProjectWeeklyLimits, upsertProjectWeeklyLimit,
  getTeamMembers,
  getEngineerBudgets, upsertEngineerBudget, deleteEngineerBudget,
  getMaterials, createMaterial, updateMaterial, deleteMaterial,
  getMilestones, createMilestone, updateMilestone, deleteMilestone,
} from '../api/client'
import PMSelect from '../components/PMSelect'
import PersonaPicker from '../components/PersonaPicker'
import { useTranslation } from 'react-i18next'
import { detalleError } from '../components/admin/detalle'

const MONTHS     = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const MONTH_KEYS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec']
const CURRENCIES = ['USD','EUR','CLP','GBP','BRL','ARS']
const COLORS     = ['#6366f1','#10b981','#f59e0b','#ef4444','#3b82f6','#8b5cf6','#ec4899','#14b8a6','#f97316','#84cc16']

const newEng  = () => ({ _k: Math.random(), id: null, engineer_name: '', team_member_id: null, ...Object.fromEntries(MONTH_KEYS.map(k=>[k,0])) })
const newMat  = () => ({ _k: Math.random(), id: null, description: '', quantity: 1, unit_price: 0, currency: 'USD' })
const newMs   = () => ({ _k: Math.random(), id: null, name: '', due_date: '', description: '' })
const EMPTY_INFO = { name:'', code:'', nickname:'', po_pm:'', pm_id: null, color:'#6366f1', start_date:'', end_date:'', is_active: true, is_s_curve: false, is_purchase_only: false, billing_category: '', priority: 'medium' }

// ─── small helpers ────────────────────────────────────────────────────────────
// A transient server error can answer with a page instead of a list: never map over it.
const lista = (d) => (Array.isArray(d) ? d : [])

function rowTotal(eng) { return MONTH_KEYS.reduce((s,k) => s + (parseFloat(eng[k])||0), 0) }
function matTotal(m)   { return (parseFloat(m.quantity)||0) * (parseFloat(m.unit_price)||0) }
function fmt(n)        { return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) }

// ─── sub-components ───────────────────────────────────────────────────────────
function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
    >
      {children}
    </button>
  )
}

// ─── main page ────────────────────────────────────────────────────────────────
export default function ProjectSetup() {
  const { t } = useTranslation(['engineering', 'admin'])
  const [searchParams, setSearchParams] = useSearchParams()
  const initialProjectId = searchParams.get('id')
  const [projects,     setProjects]     = useState([])
  const [teamMembers,  setTeamMembers]  = useState([])
  const [selectedId,   setSelectedId]   = useState('')   // '' = new
  const [tab,          setTab]          = useState('info')
  const [loading,      setLoading]      = useState(false)
  const [saving,       setSaving]       = useState(false)
  const [saved,        setSaved]        = useState(false)
  const [error,        setError]        = useState('')

  const [info,         setInfo]         = useState(EMPTY_INFO)
  const [engYear,      setEngYear]      = useState(new Date().getFullYear())
  const [engineers,    setEngineers]    = useState([newEng()])
  const [materials,    setMaterials]    = useState([newMat()])
  const [milestones,   setMilestones]   = useState([newMs()])
  // Create mode: the next correlative code ("YYYY-NNN"), shown pre-filled.
  // Saved as it is (or empty), the backend assigns the code itself -- so
  // two admins creating at once don't collide on the suggestion.
  const [suggestedCode, setSuggestedCode] = useState('')

  const suggestCode = () => getNextProjectCode()
    .then(({ code }) => {
      setSuggestedCode(code || '')
      setInfo(f => (f.code ? f : { ...f, code: code || '' }))
    })
    .catch(() => setSuggestedCode(''))

  // Load projects + team on mount; if initialProjectId given, pre-load that project
  useEffect(() => {
    setLoading(true)
    Promise.all([getProjects(), getTeamMembers()]).then(async ([ps0, ms0]) => {
      const ps = lista(ps0)
      const ms = lista(ms0)
      if (!Array.isArray(ps0) || !Array.isArray(ms0)) throw new Error('bad-response')
      setProjects(ps)
      setTeamMembers(ms)
      if (initialProjectId) {
        const pid = parseInt(initialProjectId)
        setSelectedId(String(pid))
        const p = ps.find(pp => pp.id === pid)
        if (p) {
          setInfo({ name: p.name, code: p.code||'', nickname: p.nickname||'', po_pm: p.po_pm||'', pm_id: p.pm_id ?? null, color: p.color, start_date: p.start_date||'', end_date: p.end_date||'', is_active: p.is_active, is_s_curve: p.is_s_curve||false, is_purchase_only: p.is_purchase_only||false, billing_category: p.billing_category||'', priority: p.priority || 'medium' })
        }
        const [engs, mats, msList] = (await Promise.all([
          getEngineerBudgets(pid, new Date().getFullYear()),
          getMaterials(pid),
          getMilestones(pid),
        ])).map(lista)
        setEngineers(engs.length ? engs.map(e => ({ ...e, _k: e.id })) : [newEng()])
        setMaterials(mats.length ? mats.map(m => ({ ...m, _k: m.id })) : [newMat()])
        setMilestones(msList.length ? msList.map(m => ({ ...m, _k: m.id })) : [newMs()])
      } else {
        suggestCode()
      }
      setLoading(false)
    }).catch(() => {
      setError(t('projectSetup.loadFailed'))
      setLoading(false)
    })
  }, [])

  // Reload engineer budgets when year changes (edit mode)
  useEffect(() => {
    if (selectedId) {
      getEngineerBudgets(parseInt(selectedId), engYear).then(lista).then(engs => {
        setEngineers(engs.length ? engs.map(e => ({ ...e, _k: e.id })) : [newEng()])
      })
    }
  }, [engYear, selectedId])

  const loadProjectData = async (id) => {
    setLoading(true)
    const p = projects.find(p => p.id === id)
    if (p) {
      setInfo({ name: p.name, code: p.code||'', nickname: p.nickname||'', po_pm: p.po_pm||'', pm_id: p.pm_id ?? null, color: p.color, start_date: p.start_date||'', end_date: p.end_date||'', is_active: p.is_active, is_s_curve: p.is_s_curve||false, is_purchase_only: p.is_purchase_only||false, billing_category: p.billing_category||'', priority: p.priority || 'medium' })
    }
    const [engs, mats, ms] = (await Promise.all([
      getEngineerBudgets(id, engYear),
      getMaterials(id),
      getMilestones(id),
    ])).map(lista)
    setEngineers(engs.length ? engs.map(e => ({ ...e, _k: e.id })) : [newEng()])
    setMaterials(mats.length ? mats.map(m => ({ ...m, _k: m.id })) : [newMat()])
    setMilestones(ms.length  ? ms.map(m  => ({ ...m,  _k: m.id  })) : [newMs()])
    setLoading(false)
  }

  const handleSelect = async (e) => {
    const val = e.target.value
    setSelectedId(val)
    setError(''); setSaved(false)
    if (val === '') {
      setSearchParams({}, { replace: true })
      setInfo(EMPTY_INFO)
      suggestCode()
      setEngineers([newEng()]); setMaterials([newMat()]); setMilestones([newMs()])
    } else {
      setSearchParams({ id: val }, { replace: true })
      await loadProjectData(parseInt(val))
    }
  }

  // ── Save All ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!info.name.trim()) { setError(t('projectSetup.nameRequired')); return }
    setSaving(true); setError(''); setSaved(false)
    try {
      let pid = selectedId ? parseInt(selectedId) : null

      // 1. Project info
      const payload = { ...info, code: info.code.trim()||null, nickname: info.nickname.trim()||null, po_pm: (info.po_pm||'').trim()||null, pm_id: info.pm_id ?? null, start_date: info.start_date||null, end_date: info.end_date||null, is_s_curve: info.is_s_curve, billing_category: (info.billing_category||'').trim()||null, priority: info.priority }
      if (!pid) {
        // Left as suggested (or empty): the backend takes the next code.
        if (!payload.code || payload.code === suggestedCode) payload.code = null
        const created = await createProject(payload)
        pid = created.id
        setSelectedId(String(pid))
        setSuggestedCode('')
        setInfo(f => ({ ...f, code: created.code || '' }))
        setSearchParams({ id: String(pid) }, { replace: true })
        setProjects(ps => [...ps, created])
      } else {
        await updateProject(pid, payload)
      }

      // 2. Engineers
      // The engineer is a person (team_member_id); engineer_name is their name.
      for (const eng of engineers.filter(e => e.team_member_id != null || e.engineer_name.trim())) {
        await upsertEngineerBudget({
          project_id: pid,
          team_member_id: eng.team_member_id ?? null,
          engineer_name: eng.engineer_name.trim(),
          year: engYear,
          ...Object.fromEntries(MONTH_KEYS.map(k => [k, parseFloat(eng[k])||0])),
        })
      }

      // 3. Materials
      for (const mat of materials.filter(m => m.description.trim())) {
        const mp = { description: mat.description, quantity: parseFloat(mat.quantity)||1, unit_price: parseFloat(mat.unit_price)||0, currency: mat.currency }
        if (mat.id) await updateMaterial(mat.id, mp)
        else await createMaterial({ project_id: pid, ...mp })
      }

      // 4. Milestones
      for (const ms of milestones.filter(m => m.name.trim())) {
        const mp = { name: ms.name, due_date: ms.due_date||null, description: ms.description||null }
        if (ms.id) await updateMilestone(ms.id, mp)
        else await createMilestone({ project_id: pid, ...mp })
      }

      setSaved(true)
      await loadProjectData(pid)
    } catch (e) {
      setError(detalleError(e, 'Save failed'))
    }
    setSaving(false)
  }

  // ── Engineer helpers ──────────────────────────────────────────────────────
  const updateEng  = (k, field, val) => setEngineers(es => es.map(e => e._k === k ? { ...e, [field]: val } : e))
  const removeEng  = async (eng) => {
    if (eng.id) await deleteEngineerBudget(eng.id)
    setEngineers(es => es.filter(e => e._k !== eng._k))
  }

  // ── Material helpers ──────────────────────────────────────────────────────
  const updateMat  = (k, field, val) => setMaterials(ms => ms.map(m => m._k === k ? { ...m, [field]: val } : m))
  const removeMat  = async (mat) => {
    if (mat.id) await deleteMaterial(mat.id)
    setMaterials(ms => ms.filter(m => m._k !== mat._k))
  }

  // ── Milestone helpers ─────────────────────────────────────────────────────
  const updateMs   = (k, field, val) => setMilestones(ms => ms.map(m => m._k === k ? { ...m, [field]: val } : m))
  const removeMs   = async (ms) => {
    if (ms.id) await deleteMilestone(ms.id)
    setMilestones(list => list.filter(m => m._k !== ms._k))
  }

  // ── Computed totals ───────────────────────────────────────────────────────
  const matTotalsByCurrency = materials.reduce((acc, m) => {
    if (!m.description.trim()) return acc
    const total = matTotal(m)
    acc[m.currency] = (acc[m.currency]||0) + total
    return acc
  }, {})

  const engMonthTotals = MONTH_KEYS.map(k =>
    engineers.reduce((s, e) => s + (parseFloat(e[k])||0), 0)
  )

  const inputCls = 'border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200'
  const smallInputCls = 'w-14 border border-gray-200 rounded px-1.5 py-1 text-xs text-center focus:outline-none focus:ring-1 focus:ring-gray-200'

  return (
    <div className="p-8">

      {/* Page header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="min-w-0">
          <Link to="/admin/proyectos" className="text-xs text-gray-400 hover:text-gray-700">← {t('admin:proyectos.volver')}</Link>
          <h1 className="text-[28px] font-bold text-gray-900 tracking-tight leading-tight">{t('projectSetup.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('projectSetup.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedId}
            onChange={handleSelect}
            className={`${inputCls} min-w-52`}
          >
            <option value="">{t('projectSetup.newProject')}</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>
                {p.code ? `[${p.code}] ` : ''}{p.nickname || p.name}
              </option>
            ))}
          </select>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-gray-900 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            {saving ? t('projectSetup.saving') : saved ? t('projectSetup.saved') : t('projectSetup.saveAll')}
          </button>
        </div>
      </div>

      <div>

        {error && <p className="mb-4 text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg border border-red-100">{error}</p>}

        {/* Tabs */}
        <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl w-fit">
          <TabBtn active={tab==='info'}       onClick={() => setTab('info')}>{t('projectSetup.tabInfo')}</TabBtn>
          <TabBtn active={tab==='engineers'}  onClick={() => setTab('engineers')}>{t('projectSetup.tabEngineers')}</TabBtn>
          <TabBtn active={tab==='materials'}  onClick={() => setTab('materials')}>{t('projectSetup.tabMaterials')}</TabBtn>
          <TabBtn active={tab==='milestones'} onClick={() => setTab('milestones')}>{t('projectSetup.tabMilestones')}</TabBtn>
          <TabBtn active={tab==='monthlyHours'} onClick={() => setTab('monthlyHours')}>{t('projectSetup.tabMonthlyHours')}</TabBtn>
        </div>

      {loading ? (
        <div className="text-gray-400 text-sm py-8">{t('projectSetup.loading')}</div>
      ) : (
        <>
          {/* ── INFO TAB ─────────────────────────────────────────────────── */}
          {tab === 'info' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4 max-w-lg mx-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('projectSetup.fullName')}</label>
                <input className={`w-full ${inputCls}`} value={info.name} onChange={e => setInfo(f=>({...f,name:e.target.value}))} placeholder="2026-047-ETANOL-FRETAMENTO" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('projectSetup.projectId')} <span className="text-gray-400 font-normal">{t('projectSetup.projectIdHint')}</span></label>
                <input className={`w-full font-mono ${inputCls}`} value={info.code} onChange={e => setInfo(f=>({...f,code:e.target.value}))} placeholder={suggestedCode || '2026-047'} />
                {!selectedId && suggestedCode && (
                  <p className="text-xs text-gray-400 mt-1">{t('admin:proyectos.codigoSugerido', { code: suggestedCode })}</p>
                )}
              </div>
              {!info.is_purchase_only && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('projectSetup.poPmo')}</label>
                  <PMSelect value={info.pm_id} nombre={info.po_pm} members={teamMembers}
                    onChange={(id, nombre) => setInfo(f => ({ ...f, pm_id: id, po_pm: nombre || '' }))} />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('projectSetup.nickname')} <span className="text-gray-400 font-normal">{t('projectSetup.nicknameHint')}</span></label>
                <input className={`w-full ${inputCls}`} value={info.nickname} onChange={e => setInfo(f=>({...f,nickname:e.target.value}))} placeholder={t('projectSetup.nicknamePlaceholder')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('projectSetup.color')}</label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map(c => (
                    <button key={c} onClick={() => setInfo(f=>({...f,color:c}))}
                      className={`w-7 h-7 rounded-full transition-transform ${info.color===c ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-110'}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('projectSetup.priority')}</label>
                <select
                  value={info.priority}
                  onChange={e => setInfo(f => ({ ...f, priority: e.target.value }))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                >
                  <option value="high">{t('projectSetup.priorityHigh')}</option>
                  <option value="medium">{t('projectSetup.priorityMedium')}</option>
                  <option value="low">{t('projectSetup.priorityLow')}</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('projectSetup.startDate')}</label>
                  <input type="date" className={`w-full ${inputCls}`} value={info.start_date} onChange={e => setInfo(f=>({...f,start_date:e.target.value}))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('projectSetup.endDate')}</label>
                  <input type="date" className={`w-full ${inputCls}`} value={info.end_date} onChange={e => setInfo(f=>({...f,end_date:e.target.value}))} />
                </div>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-sm font-medium text-gray-700">{t('projectSetup.active')}</span>
                <button type="button" onClick={() => setInfo(f=>({...f,is_active:!f.is_active}))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${info.is_active ? 'bg-gray-900' : 'bg-gray-300'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${info.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              <div className="flex items-center justify-between py-1">
                <div>
                  <span className="text-sm font-medium text-gray-700">{t('projectSetup.partOfSCurve')}</span>
                  <p className="text-xs text-gray-400">{t('projectSetup.sCurveHint')}</p>
                </div>
                <button type="button" onClick={() => setInfo(f=>({...f,is_s_curve:!f.is_s_curve}))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${info.is_s_curve ? 'bg-gray-900' : 'bg-gray-300'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${info.is_s_curve ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              <div className="flex items-center justify-between py-1">
                <div>
                  <span className="text-sm font-medium text-gray-700">{t('projectSetup.purchaseOnly')}</span>
                  <p className="text-xs text-gray-400">{t('projectSetup.purchaseOnlyHint')}</p>
                </div>
                <button type="button"
                  onClick={() => setInfo(f => ({ ...f, is_purchase_only: !f.is_purchase_only, po_pm: !f.is_purchase_only ? '' : f.po_pm, pm_id: !f.is_purchase_only ? null : f.pm_id }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${info.is_purchase_only ? 'bg-gray-900' : 'bg-gray-300'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${info.is_purchase_only ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('projectSetup.billingCategory')}</label>
                <input
                  className={`w-full ${inputCls}`}
                  value={info.billing_category || ''}
                  onChange={e => setInfo(f=>({...f, billing_category: e.target.value}))}
                  placeholder="e.g. R&D, BC Attivi HAE"
                />
                <p className="text-xs text-gray-400 mt-1">{t('projectSetup.billingCategoryHint')}</p>
              </div>
            </div>
          )}

          {/* ── ENGINEERS TAB ─────────────────────────────────────────────── */}
          {tab === 'engineers' && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Year selector */}
              <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">{t('projectSetup.year')}</span>
                <select value={engYear} onChange={e => setEngYear(Number(e.target.value))}
                  className={inputCls}>
                  {[2024,2025,2026,2027,2028].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <span className="text-xs text-gray-400">{t('projectSetup.hoursAutoSynced')}</span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-separate border-spacing-0">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="sticky left-0 bg-gray-50 border-b border-r border-gray-200 px-4 py-2.5 text-left text-xs font-semibold text-gray-600 min-w-44">{t('projectSetup.engineerColumn')}</th>
                      {MONTHS.map(m => (
                        <th key={m} className="border-b border-gray-200 px-1 py-2.5 text-center text-xs font-semibold text-gray-600 min-w-14">{m}</th>
                      ))}
                      <th className="border-b border-gray-200 px-3 py-2.5 text-center text-xs font-semibold text-gray-600 min-w-16">{t('projectSetup.totalColumn')}</th>
                      <th className="border-b border-gray-200 w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {engineers.map((eng, i) => (
                      <tr key={eng._k} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                        <td className={`sticky left-0 border-b border-r border-gray-100 px-3 py-2 ${i%2===0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                          <PersonaPicker className="w-56" value={eng.team_member_id ?? null}
                            personas={teamMembers.length ? teamMembers : undefined}
                            placeholder={eng.engineer_name || t('projectSetup.selectEngineer')}
                            onChange={(id, p) => setEngineers(es => es.map(x => x._k === eng._k
                              ? { ...x, team_member_id: id ?? null, engineer_name: p ? p.name : '' } : x))} />
                        </td>
                        {MONTH_KEYS.map(k => (
                          <td key={k} className="border-b border-gray-100 px-1 py-2 text-center">
                            <input
                              type="number" min="0" step="1"
                              className={smallInputCls}
                              value={eng[k] || ''}
                              onChange={e => updateEng(eng._k, k, e.target.value)}
                              placeholder="0"
                            />
                          </td>
                        ))}
                        <td className="border-b border-gray-100 px-3 py-2 text-center text-sm font-bold text-gray-900">
                          {rowTotal(eng)}
                        </td>
                        <td className="border-b border-gray-100 px-2 py-2 text-center">
                          <button onClick={() => removeEng(eng)} className="text-gray-300 hover:text-red-500 transition-colors text-base leading-none">×</button>
                        </td>
                      </tr>
                    ))}

                    {/* Monthly totals row */}
                    {engineers.length > 1 && (
                      <tr className="bg-gray-50">
                        <td className="sticky left-0 bg-gray-50 px-4 py-2 border-t border-gray-200 text-xs font-semibold text-gray-700">{t('projectSetup.totalColumn')}</td>
                        {engMonthTotals.map((tot, i) => (
                          <td key={i} className="px-1 py-2 text-center border-t border-gray-200 text-xs font-semibold text-gray-700">{tot||''}</td>
                        ))}
                        <td className="px-3 py-2 text-center border-t border-gray-200 text-xs font-bold text-gray-700">
                          {engMonthTotals.reduce((s,n)=>s+n,0)}
                        </td>
                        <td className="border-t border-gray-200" />
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="px-5 py-3 border-t border-gray-100">
                <button onClick={() => setEngineers(es => [...es, newEng()])}
                  className="text-sm text-gray-700 hover:text-gray-900 font-medium">{t('projectSetup.addEngineer')}</button>
              </div>
            </div>
          )}

          {/* ── MATERIALS TAB ─────────────────────────────────────────────── */}
          {tab === 'materials' && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">{t('projectSetup.descriptionColumn')}</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-gray-600 w-20">{t('projectSetup.qtyColumn')}</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-gray-600 w-28">{t('projectSetup.unitPriceColumn')}</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-gray-600 w-24">{t('projectSetup.currencyColumn')}</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 w-28">{t('projectSetup.totalMaterialsColumn')}</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {materials.map((mat, i) => (
                    <tr key={mat._k} className={`border-b border-gray-50 ${i%2===0?'bg-white':'bg-gray-50/50'}`}>
                      <td className="px-4 py-2">
                        <input className={`w-full ${inputCls}`} value={mat.description}
                          onChange={e => updateMat(mat._k, 'description', e.target.value)}
                          placeholder={t('projectSetup.descriptionPlaceholder')} />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" min="0" step="0.01" className={`w-full text-center ${inputCls}`}
                          value={mat.quantity}
                          onChange={e => updateMat(mat._k, 'quantity', e.target.value)} />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" min="0" step="0.01" className={`w-full text-right ${inputCls}`}
                          value={mat.unit_price}
                          onChange={e => updateMat(mat._k, 'unit_price', e.target.value)} />
                      </td>
                      <td className="px-3 py-2">
                        <select className={`w-full ${inputCls}`} value={mat.currency}
                          onChange={e => updateMat(mat._k, 'currency', e.target.value)}>
                          {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-2 text-right font-semibold text-gray-800">
                        {mat.description.trim() ? fmt(matTotal(mat)) : '—'}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <button onClick={() => removeMat(mat)} className="text-gray-300 hover:text-red-500 transition-colors text-base leading-none">×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                <button onClick={() => setMaterials(ms => [...ms, newMat()])}
                  className="text-sm text-gray-700 hover:text-gray-900 font-medium">{t('projectSetup.addItem')}</button>
                {Object.keys(matTotalsByCurrency).length > 0 && (
                  <div className="flex items-center gap-4 text-sm font-semibold text-gray-700">
                    <span className="text-xs text-gray-400 font-normal">{t('projectSetup.totalLabel')}</span>
                    {Object.entries(matTotalsByCurrency).map(([cur, tot]) => (
                      <span key={cur}>{cur} {fmt(tot)}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── MILESTONES TAB ────────────────────────────────────────────── */}
          {tab === 'milestones' && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">{t('projectSetup.milestoneColumn')}</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-gray-600 w-36">{t('projectSetup.dueDateColumn')}</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-gray-600">{t('projectSetup.descriptionMilestoneColumn')}</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {milestones.map((ms, i) => (
                    <tr key={ms._k} className={`border-b border-gray-50 ${i%2===0?'bg-white':'bg-gray-50/50'}`}>
                      <td className="px-4 py-2">
                        <input className={`w-full ${inputCls}`} value={ms.name}
                          onChange={e => updateMs(ms._k, 'name', e.target.value)}
                          placeholder={t('projectSetup.milestonePlaceholder')} />
                      </td>
                      <td className="px-3 py-2">
                        <input type="date" className={`w-full ${inputCls}`} value={ms.due_date}
                          onChange={e => updateMs(ms._k, 'due_date', e.target.value)} />
                      </td>
                      <td className="px-3 py-2">
                        <input className={`w-full ${inputCls}`} value={ms.description}
                          onChange={e => updateMs(ms._k, 'description', e.target.value)}
                          placeholder={t('projectSetup.milestoneDescPlaceholder')} />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <button onClick={() => removeMs(ms)} className="text-gray-300 hover:text-red-500 transition-colors text-base leading-none">×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="px-4 py-3 border-t border-gray-100">
                <button onClick={() => setMilestones(ms => [...ms, newMs()])}
                  className="text-sm text-gray-700 hover:text-gray-900 font-medium">{t('projectSetup.addMilestone')}</button>
              </div>
            </div>
          )}

          {/* ── MONTHLY HOURS TAB ─────────────────────────────────────────── */}
          {tab === 'monthlyHours' && (
            selectedId ? (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <MonthlyHoursPanel projectId={parseInt(selectedId)} t={t} />
              </div>
            ) : (
              <div className="text-sm text-gray-400">{t('projectSetup.nameRequired')}</div>
            )
          )}
        </>
      )}

      </div>
    </div>
  )
}

function MonthlyHoursPanel({ projectId, t }) {
  const today = new Date()
  const [year, setYear] = useState(today.getFullYear())
  const [rows, setRows] = useState({})   // month(1-12) -> { min_weekly_hours, max_weekly_hours }
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getProjectWeeklyLimits(projectId, year).then(data => {
      if (cancelled) return
      const map = {}
      data.forEach(b => { map[b.month] = { min_weekly_hours: b.min_weekly_hours, max_weekly_hours: b.max_weekly_hours } })
      setRows(map)
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [projectId, year])

  const save = async (month, field, value) => {
    const num = value === '' ? null : Number(value)
    const current = rows[month] || { min_weekly_hours: null, max_weekly_hours: null }
    const next = { ...current, [field]: num }
    setRows(prev => ({ ...prev, [month]: next }))
    await upsertProjectWeeklyLimit({
      project_id: projectId, year, month,
      min_weekly_hours: next.min_weekly_hours, max_weekly_hours: next.max_weekly_hours,
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{t('projectSetup.monthlyHoursHint')}</p>
        <select
          value={year}
          onChange={e => setYear(Number(e.target.value))}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
        >
          {[2024, 2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      {loading ? (
        <div className="text-sm text-gray-400">{t('projectSetup.loading')}</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">{t('projectSetup.monthColumn')}</th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">{t('projectSetup.minColumn')}</th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">{t('projectSetup.maxColumn')}</th>
            </tr>
          </thead>
          <tbody>
            {MONTHS.map((label, i) => {
              const month = i + 1
              const row = rows[month] || {}
              return (
                <tr key={month} className="border-b border-gray-50">
                  <td className="px-4 py-1.5 text-gray-700">{label}</td>
                  <td className="px-4 py-1.5">
                    <input
                      type="number" min="0"
                      defaultValue={row.min_weekly_hours ?? ''}
                      onBlur={e => save(month, 'min_weekly_hours', e.target.value)}
                      className="w-24 border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                    />
                  </td>
                  <td className="px-4 py-1.5">
                    <input
                      type="number" min="0"
                      defaultValue={row.max_weekly_hours ?? ''}
                      onBlur={e => save(month, 'max_weekly_hours', e.target.value)}
                      className="w-24 border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
